import { Button, NativeSelect, Text, VStack, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { campaignApi } from '../../lib/api'
import { useCreateTurf } from '../../hooks/useAnalyticsMap'
import { extractApiMessage } from '../../lib/apiError'
import type { ScopeType, BoundingBox } from '../../types/analyticsMap'
import type { FilterGroupRequest } from '../../types/contactList'

interface CreateTurfModalProps {
  isOpen: boolean
  onClose: () => void
  scope: ScopeType
  filterGroups: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  thresholds?: Record<string, number>
  /** Auto-set turf label (e.g. sim-driven "Sim targets — …"); defaults to '' so the backend names it. */
  label?: string
  onExported?: (campaignId: string, merged: boolean) => void
}

export function CreateTurfModal({
  isOpen,
  onClose,
  scope,
  filterGroups,
  profileNames,
  polygonWkt,
  bBox,
  thresholds,
  label = '',
  onExported,
}: CreateTurfModalProps) {
  const [campaignId, setCampaignId] = useState('')
  const createMutation = useCreateTurf()

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignApi.getAll().then(r => r.data),
  })

  const handleCreate = () => {
    if (!campaignId) return
    createMutation.mutate(
      {
        filterGroups,
        profileNames,
        polygonWkt,
        bBox,
        scope,
        thresholds,
        campaignId,
        label,
      },
      {
        onSuccess: (result) => {
          if (result.merged) {
            toaster.create({
              title: 'Contacts merged',
              description: `Added ${result.contactCount} contacts to campaign turfs.`,
              type: 'success',
              duration: 4000,
              closable: true,
            })
          } else {
            toaster.create({
              title: 'Turf created',
              description: `Created turf with ${result.contactCount} contacts.`,
              type: 'success',
              duration: 4000,
              closable: true,
            })
          }
          const exportedCampaignId = campaignId
          setCampaignId('')
          onClose()
          onExported?.(exportedCampaignId, result.merged)
        },
        onError: (error) => {
          toaster.create({
            title: 'Failed to create turf',
            description: extractApiMessage(error, 'Could not create turf. Please try again.'),
            type: 'error',
            duration: 4000,
            closable: true,
          })
        },
      }
    )
  }

  const handleClose = () => {
    setCampaignId('')
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="md">Export Contacts to Campaign</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Export the selected contacts to a campaign. If the campaign already has turfs, the contacts will be merged into the existing pool.
                </Text>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    placeholder="Select campaign..."
                    value={campaignId}
                    onChange={e => setCampaignId(e.target.value)}>
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleCreate}
                loading={createMutation.isPending}
                disabled={!campaignId}
              >
                Export to Campaign
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
